import { useState, useEffect } from 'react';
import { searchArticles } from '../services/manual.service';
import type { ManualArticle } from '../lib/supabase.types';

interface ManualSearchProps {
  onNavigate: (path: string) => void;
  language?: string;
}

export default function ManualSearch({ onNavigate, language = 'ja' }: ManualSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ManualArticle[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const search = async () => {
      setLoading(true);
      try {
        const articles = await searchArticles(query, language);
        setResults(articles);
      } catch (error) {
        console.error('Failed to search articles:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(search, 300);
    return () => clearTimeout(debounceTimer);
  }, [query, language]);

  return (
    <div className="manual-search">
      <input
        type="text"
        className="manual-search-input"
        placeholder={language === 'en' ? 'Search manual...' : 'マニュアル内を検索...'}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {loading && query.trim() && (
        <div className="manual-search-loading">
          {language === 'en' ? 'Searching...' : '検索中...'}
        </div>
      )}

      {results.length > 0 && (
        <div className="manual-search-results">
          {results.map((article) => (
            <button
              key={article.slug}
              className="manual-search-result-item"
              onClick={() => {
                onNavigate(`/manual/article/${article.slug}`);
                setQuery('');
              }}
            >
              <div className="manual-search-result-section">{article.section_id}</div>
              <div className="manual-search-result-title">{article.title}</div>
              {article.description && (
                <div className="manual-search-result-summary">{article.description}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
